import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const GuestAuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Guest Authentication Context using Emergent Google OAuth
 * 
 * This handles guest sign-in via Google, separate from the admin CMS auth.
 * Guests can sign in to save favorites, get personalized recommendations, etc.
 */
export const GuestAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);

  // CRITICAL: If returning from OAuth callback, skip the /me check.
  // AuthCallback will exchange the session_id and establish the session first.
  const checkAuth = useCallback(async () => {
    // Skip if we're processing OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/google/me`, {
        credentials: 'include' // Send cookies
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Fetch favorites after successful auth
        fetchFavorites();
      } else {
        setUser(null);
        setFavorites([]);
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setUser(null);
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google/favorites`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    // Use current origin to build redirect URL - DO NOT hardcode or add fallbacks
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/google/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setFavorites([]);
  };

  const processOAuthCallback = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (!response.ok) {
        throw new Error('Failed to exchange session');
      }

      const userData = await response.json();
      setUser(userData);
      // Fetch favorites after login
      fetchFavorites();
      return userData;
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  };

  const toggleFavorite = async (propertyId) => {
    if (!user) {
      // Not logged in, redirect to login
      loginWithGoogle();
      return false;
    }

    const isFavorited = favorites.includes(propertyId);
    
    try {
      if (isFavorited) {
        // Remove from favorites
        await fetch(`${API_URL}/api/auth/google/favorites/${propertyId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        setFavorites(prev => prev.filter(id => id !== propertyId));
      } else {
        // Add to favorites
        await fetch(`${API_URL}/api/auth/google/favorites/${propertyId}`, {
          method: 'POST',
          credentials: 'include'
        });
        setFavorites(prev => [...prev, propertyId]);
      }
      return !isFavorited;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return isFavorited;
    }
  };

  const isFavorite = (propertyId) => favorites.includes(propertyId);

  const trackPropertyView = async (propertyId) => {
    if (!user) return;
    try {
      await fetch(`${API_URL}/api/auth/google/history/${propertyId}`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      // Silent fail for tracking
    }
  };

  const getRecommendations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google/recommendations`, {
        credentials: 'include'
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error getting recommendations:', error);
    }
    return { recommendations: [], reason: 'error' };
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    favorites,
    loginWithGoogle,
    logout,
    processOAuthCallback,
    refreshAuth: checkAuth,
    toggleFavorite,
    isFavorite,
    trackPropertyView,
    getRecommendations
  };

  return (
    <GuestAuthContext.Provider value={value}>
      {children}
    </GuestAuthContext.Provider>
  );
};

export const useGuestAuth = () => {
  const context = useContext(GuestAuthContext);
  if (!context) {
    throw new Error('useGuestAuth must be used within a GuestAuthProvider');
  }
  return context;
};

export default GuestAuthContext;
